import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  AttachMoney as BudgetIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

const BudgetOverview = ({ tasks, projects }) => {
  const { translate } = useLanguage();
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [expenseData, setExpenseData] = useState({
    amount: '',
    description: '',
    category: 'development'
  });

  // Calculate budget statistics
  const totalBudget = projects.reduce((sum, p) => sum + p.budget, 0);
  const totalSpent = projects.reduce((sum, p) => sum + p.spent, 0);
  const remainingBudget = totalBudget - totalSpent;
  const budgetUtilization = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

  // Task budget statistics
  const taskBudget = tasks.reduce((sum, t) => sum + t.budget, 0);
  const taskSpent = tasks.reduce((sum, t) => sum + t.spent, 0);
  const taskRemaining = taskBudget - taskSpent;

  // Over-budget projects
  const overBudgetProjects = projects.filter(p => p.spent > p.budget);
  const overBudgetTasks = tasks.filter(t => t.spent > t.budget);

  const handleAddExpense = () => {
    if (expenseData.amount && expenseData.description && selectedProject) {
      // TODO: Add expense to backend
      console.log('Adding expense:', { ...expenseData, project: selectedProject });
      setExpenseData({ amount: '', description: '', category: 'development' });
      setSelectedProject('');
      setShowAddExpense(false);
    }
  };

  const getBudgetStatusColor = (spent, budget) => {
    const percentage = (spent / budget) * 100;
    if (percentage >= 100) return 'error';
    if (percentage >= 80) return 'warning';
    return 'success';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const expenseCategories = [
    'development', 'design', 'testing', 'deployment', 'hardware',
    'software', 'consulting', 'training', 'travel', 'other'
  ];

  return (
    <Box>
      {/* Budget Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BudgetIcon color="primary" />
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">{formatCurrency(totalBudget)}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Budget</Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={Math.min(budgetUtilization, 100)} 
                color={getBudgetStatusColor(totalSpent, totalBudget)}
                sx={{ mb: 1 }}
              />
              <Typography variant="caption">
                {budgetUtilization.toFixed(1)}% utilized
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingDownIcon color="success" />
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">{formatCurrency(remainingBudget)}</Typography>
                  <Typography variant="body2" color="text.secondary">Remaining</Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatCurrency(totalSpent)} spent
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon color="info" />
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">{formatCurrency(taskBudget)}</Typography>
                  <Typography variant="body2" color="text.secondary">Task Budget</Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={taskBudget > 0 ? (taskSpent / taskBudget) * 100 : 0} 
                sx={{ mb: 1 }}
              />
              <Typography variant="caption">
                {formatCurrency(taskSpent)} spent
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon color="warning" />
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">{overBudgetProjects.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Over Budget</Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {overBudgetTasks.length} tasks over budget
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Project Budget Table */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Project Budgets</Typography>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => setShowAddExpense(true)}
            >
              Add Expense
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project</TableCell>
                  <TableCell align="right">Budget</TableCell>
                  <TableCell align="right">Spent</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell align="center">Utilization</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((project) => {
                  const remaining = project.budget - project.spent;
                  const utilization = (project.spent / project.budget) * 100;
                  const isOverBudget = project.spent > project.budget;

                  return (
                    <TableRow key={project.id}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {project.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(project.budget)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          color={isOverBudget ? 'error.main' : 'text.primary'}
                        >
                          {formatCurrency(project.spent)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          color={remaining < 0 ? 'error.main' : 'text.secondary'}
                        >
                          {formatCurrency(remaining)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(utilization, 100)}
                            color={getBudgetStatusColor(project.spent, project.budget)}
                            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption">
                            {utilization.toFixed(1)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={isOverBudget ? 'Over Budget' : 'On Track'}
                          color={isOverBudget ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Task Budget Summary */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Task Budget Summary</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Budget by Status</Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Completed</Typography>
                  <Typography variant="body2">
                    {formatCurrency(tasks.filter(t => t.status === 'completed').reduce((sum, t) => sum + t.spent, 0))}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={taskBudget > 0 ? (tasks.filter(t => t.status === 'completed').reduce((sum, t) => sum + t.spent, 0) / taskBudget) * 100 : 0} 
                  sx={{ mb: 2 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">In Progress</Typography>
                  <Typography variant="body2">
                    {formatCurrency(tasks.filter(t => t.status === 'in_progress').reduce((sum, t) => sum + t.spent, 0))}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={taskBudget > 0 ? (tasks.filter(t => t.status === 'in_progress').reduce((sum, t) => sum + t.spent, 0) / taskBudget) * 100 : 0} 
                  sx={{ mb: 2 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Pending</Typography>
                  <Typography variant="body2">
                    {formatCurrency(tasks.filter(t => t.status === 'pending').reduce((sum, t) => sum + t.spent, 0))}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={taskBudget > 0 ? (tasks.filter(t => t.status === 'pending').reduce((sum, t) => sum + t.spent, 0) / taskBudget) * 100 : 0} 
                  sx={{ mb: 2 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Budget by Priority</Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">High Priority</Typography>
                  <Typography variant="body2">
                    {formatCurrency(tasks.filter(t => t.priority === 'high').reduce((sum, t) => sum + t.spent, 0))}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={taskBudget > 0 ? (tasks.filter(t => t.priority === 'high').reduce((sum, t) => sum + t.spent, 0) / taskBudget) * 100 : 0} 
                  sx={{ mb: 2 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Medium Priority</Typography>
                  <Typography variant="body2">
                    {formatCurrency(tasks.filter(t => t.priority === 'medium').reduce((sum, t) => sum + t.spent, 0))}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={taskBudget > 0 ? (tasks.filter(t => t.priority === 'medium').reduce((sum, t) => sum + t.spent, 0) / taskBudget) * 100 : 0} 
                  sx={{ mb: 2 }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Low Priority</Typography>
                  <Typography variant="body2">
                    {formatCurrency(tasks.filter(t => t.priority === 'low').reduce((sum, t) => sum + t.spent, 0))}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={taskBudget > 0 ? (tasks.filter(t => t.priority === 'low').reduce((sum, t) => sum + t.spent, 0) / taskBudget) * 100 : 0} 
                  sx={{ mb: 2 }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Add Expense Dialog */}
      <Dialog
        open={showAddExpense}
        onClose={() => setShowAddExpense(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Expense</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                label="Project"
              >
                {projects.map(project => (
                  <MenuItem key={project.id} value={project.name}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Amount ($)"
              type="number"
              value={expenseData.amount}
              onChange={(e) => setExpenseData({ ...expenseData, amount: e.target.value })}
              sx={{ mb: 2 }}
              inputProps={{ min: 0, step: 0.01 }}
            />

            <TextField
              fullWidth
              label="Description"
              value={expenseData.description}
              onChange={(e) => setExpenseData({ ...expenseData, description: e.target.value })}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={expenseData.category}
                onChange={(e) => setExpenseData({ ...expenseData, category: e.target.value })}
                label="Category"
              >
                {expenseCategories.map(category => (
                  <MenuItem key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddExpense(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddExpense} variant="contained">
            Add Expense
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetOverview; 